import React, { useState } from 'react';
import './App.css';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  PieChart, Pie, Cell, LineChart, Line, ScatterChart, Scatter, Treemap
} from 'recharts';
import { Activity, AlertTriangle, Users, Clock, MapPin, TrendingUp } from 'lucide-react';
import {
  waitTimesData,
  issueCategories,
  geographicData,
  trendingThemes,
  severityData,
  youtubeSources,
  redditSources,
  topKeywords
} from './data/healthcareData';

const COLORS = {
  critical: '#ff3b30',
  high: '#ff9500',
  medium: '#ffcc02',
  low: '#34c759',
  primary: '#007aff',
  secondary: '#5856d6',
  accent: '#af52de'
};

function App() {
  const [activeFilter, setActiveFilter] = useState('all');

  const filterData = (data, filter) => {
    if (filter === 'all') return data;
    return data.filter(item => item.severity?.toLowerCase() === filter);
  };

  const getSeverityColor = (severity) => {
    switch (severity?.toLowerCase()) {
      case 'critical': return COLORS.critical;
      case 'high': return COLORS.high;
      case 'medium': return COLORS.medium;
      case 'low': return COLORS.low;
      default: return COLORS.primary;
    }
  };

  return (
    <div className="App">
      <div className="header">
        <h1>Australian Healthcare Crisis Dashboard</h1>
        <p>Comprehensive analysis of healthcare system issues across Australia</p>
      </div>

      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-number">64</div>
          <div className="stat-label">Total Issues Tracked</div>
        </div>
        <div className="stat-card">
          <div className="stat-number">6</div>
          <div className="stat-label">States Affected</div>
        </div>
        <div className="stat-card">
          <div className="stat-number">47</div>
          <div className="stat-label">Wait Time Complaints</div>
        </div>
        <div className="stat-card">
          <div className="stat-number">25</div>
          <div className="stat-label">Critical Issues</div>
        </div>
      </div>

      <div className="dashboard-grid">
        <div className="chart-container">
          <h2 className="chart-title">
            <Clock className="inline mr-2" size={24} />
            Wait Times by State
          </h2>
          <p className="chart-subtitle">Emergency, elective surgery, and ambulance response times (hours)</p>
          <ResponsiveContainer width="100%" height={350}>
            <BarChart data={waitTimesData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="state" stroke="#6e6e73" />
              <YAxis stroke="#6e6e73" />
              <Tooltip 
                animationDuration={0}
                isAnimationActive={false}
              />
              <Legend />
              <Bar dataKey="emergency" fill={COLORS.critical} name="Emergency (hrs)" />
              <Bar dataKey="elective" fill={COLORS.medium} name="Elective Surgery (months)" />
              <Bar dataKey="ambulance" fill={COLORS.high} name="Ambulance (hrs)" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="chart-container">
          <h2 className="chart-title">
            <AlertTriangle className="inline mr-2" size={24} />
            Issue Categories
          </h2>
          <p className="chart-subtitle">Most frequently reported healthcare issues</p>
          <div className="filters">
            {['all', 'critical', 'high', 'medium'].map(filter => (
              <button
                key={filter}
                className={`filter-button ${activeFilter === filter ? 'active' : ''}`}
                onClick={() => setActiveFilter(filter)}
              >
                {filter.charAt(0).toUpperCase() + filter.slice(1)}
              </button>
            ))}
          </div>
          <ResponsiveContainer width="100%" height={350}>
            <BarChart data={filterData(issueCategories, activeFilter)}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="name" stroke="#6e6e73" angle={-45} textAnchor="end" height={100} />
              <YAxis stroke="#6e6e73" />
              <Tooltip 
                animationDuration={0}
                isAnimationActive={false}
              />
              <Bar dataKey="count" fill={(entry) => getSeverityColor(entry.severity)} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="chart-container">
          <h2 className="chart-title">
            <MapPin className="inline mr-2" size={24} />
            Geographic Distribution
          </h2>
          <p className="chart-subtitle">Healthcare incidents by state/territory</p>
          <ResponsiveContainer width="100%" height={350}>
            <ScatterChart data={geographicData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="state" stroke="#6e6e73" />
              <YAxis dataKey="incidents" stroke="#6e6e73" />
              <Tooltip 
                animationDuration={0}
                isAnimationActive={false}
              />
              <Scatter dataKey="incidents" fill={COLORS.primary} />
            </ScatterChart>
          </ResponsiveContainer>
        </div>

        <div className="chart-container">
          <h2 className="chart-title">
            <Activity className="inline mr-2" size={24} />
            Issue Severity Distribution
          </h2>
          <p className="chart-subtitle">Breakdown of issues by severity level</p>
          <ResponsiveContainer width="100%" height={350}>
            <PieChart>
              <Pie
                data={severityData}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ name, percentage }) => `${name}: ${percentage}%`}
                outerRadius={80}
                fill="#8884d8"
                dataKey="count"
              >
                {severityData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={getSeverityColor(entry.category.split(' ')[0])} />
                ))}
              </Pie>
              <Tooltip 
                animationDuration={0}
                isAnimationActive={false}
              />
            </PieChart>
          </ResponsiveContainer>
          <div className="legend-container">
            {Object.entries(COLORS).slice(0, 4).map(([key, color]) => (
              <div key={key} className="legend-item">
                <div className="legend-color" style={{ backgroundColor: color }}></div>
                <span>{key.charAt(0).toUpperCase() + key.slice(1)}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="chart-container">
          <h2 className="chart-title">
            <TrendingUp className="inline mr-2" size={24} />
            Trending Themes
          </h2>
          <p className="chart-subtitle">Public sentiment on healthcare issues</p>
          <ResponsiveContainer width="100%" height={350}>
            <BarChart data={trendingThemes}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="theme" stroke="#6e6e73" angle={-45} textAnchor="end" height={100} />
              <YAxis stroke="#6e6e73" />
              <Tooltip 
                animationDuration={0}
                isAnimationActive={false}
              />
              <Bar dataKey="evidence" fill={COLORS.secondary} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="chart-container">
          <h2 className="chart-title">
            <Users className="inline mr-2" size={24} />
            Top Keywords Analysis
          </h2>
          <p className="chart-subtitle">Most frequently mentioned terms in complaints</p>
          <ResponsiveContainer width="100%" height={350}>
            <BarChart data={topKeywords.slice(0, 8)}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="keyword" stroke="#6e6e73" angle={-45} textAnchor="end" height={100} />
              <YAxis stroke="#6e6e73" />
              <Tooltip 
                animationDuration={0}
                isAnimationActive={false}
              />
              <Bar dataKey="frequency" fill={COLORS.accent} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="dashboard-grid">
        <div className="chart-container">
          <h2 className="chart-title">Data Sources - YouTube</h2>
          <p className="chart-subtitle">Analysis of YouTube content and comments</p>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={youtubeSources}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="channel" stroke="#6e6e73" angle={-45} textAnchor="end" height={100} />
              <YAxis stroke="#6e6e73" />
              <Tooltip 
                animationDuration={0}
                isAnimationActive={false}
              />
              <Bar dataKey="videos" fill={COLORS.primary} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="chart-container">
          <h2 className="chart-title">Data Sources - Reddit</h2>
          <p className="chart-subtitle">Analysis of Reddit posts and discussions</p>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={redditSources}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="subreddit" stroke="#6e6e73" angle={-45} textAnchor="end" height={100} />
              <YAxis stroke="#6e6e73" />
              <Tooltip 
                animationDuration={0}
                isAnimationActive={false}
              />
              <Bar dataKey="posts" fill={COLORS.secondary} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="chart-container" style={{ marginTop: '40px' }}>
        <h2 className="chart-title">Executive Summary</h2>
        <div style={{ fontSize: '1.1rem', lineHeight: '1.6', color: '#1d1d1f' }}>
          <p style={{ marginBottom: '20px' }}>
            <strong>Critical Healthcare Crisis:</strong> Australia's healthcare system faces unprecedented challenges with emergency department wait times reaching 16+ hours, elective surgery delays extending 2-7 years, and frequent ambulance unavailability across all states.
          </p>
          <p style={{ marginBottom: '20px' }}>
            <strong>Key Issues Identified:</strong> Chronic understaffing (particularly nursing shortages), inadequate bed capacity, systemic failures in patient flow, and growing concerns about privatization impacts on care quality.
          </p>
          <p style={{ marginBottom: '20px' }}>
            <strong>Geographic Impact:</strong> NSW and Victoria report the highest incident counts, while Western Australia shows the worst urgent care wait times nationally. Regional areas face additional challenges with limited specialist access.
          </p>
          <p style={{ marginBottom: '0' }}>
            <strong>Public Sentiment:</strong> Analysis of 64+ documented cases across YouTube and Reddit reveals extremely negative sentiment toward system privatization, government inaction, and deteriorating care standards. Immediate systemic reform is urgently needed.
          </p>
        </div>
      </div>

    </div>
  );
}

export default App;
